import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { EyeIcon, SparklesIcon, ArrowRightIcon, MoonIcon, SunIcon, HeartIcon, LayersIcon, ActivityIcon, UsersIcon } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Tabs } from '../components/ui/Tabs';
import { Breadcrumbs } from '../components/ui/Breadcrumbs';
import { BetaReportPreview } from '../components/reports/BetaReportPreview';
import { PremiumReportPreview } from '../components/reports/PremiumReportPreview';
interface ReportPreviewPageProps {
  onNavigate: (page: string) => void;
  initialService?: ServiceType;
  initialReportType?: ReportType;
}
type ServiceType = 'natal' | 'solar' | 'compatibility' | 'combo' | 'forecast';
type ReportType = 'beta' | 'premium';
const SERVICE_OPTIONS: {
  value: ServiceType;
  label: string;
  icon: React.ReactNode;
}[] = [{
  value: 'natal',
  label: 'Натальная карта',
  icon: <MoonIcon className="w-4 h-4" data-id="element-21923" />
}, {
  value: 'solar',
  label: 'Соляр',
  icon: <SunIcon className="w-4 h-4" data-id="element-21924" />
}, {
  value: 'compatibility',
  label: 'Совместимость',
  icon: <HeartIcon className="w-4 h-4" data-id="element-21925" />
}, {
  value: 'combo',
  label: 'Комбо',
  icon: <LayersIcon className="w-4 h-4" data-id="element-21926" />
}, {
  value: 'forecast',
  label: 'Транзиты',
  icon: <ActivityIcon className="w-4 h-4" data-id="element-21927" />
}];
export function ReportPreviewPage({
  onNavigate,
  initialService,
  initialReportType
}: ReportPreviewPageProps) {
  const [selectedService, setSelectedService] = useState<ServiceType>(initialService || 'natal');
  const [selectedReportType, setSelectedReportType] = useState<ReportType>(initialReportType || 'beta');
  const reportTabs = [{
    id: 'beta',
    label: 'Beta отчёт',
    icon: <EyeIcon className="w-4 h-4" data-id="element-21928" />
  }, {
    id: 'premium',
    label: 'Premium отчёт',
    icon: <SparklesIcon className="w-4 h-4" data-id="element-21929" />
  }];
  return <div className="w-full bg-slate-50 min-h-screen pb-24" data-id="element-21930">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 pt-6 pb-8 print:hidden" data-id="element-21931">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-21932">
          <Breadcrumbs items={[{
          label: 'Главная',
          onClick: () => onNavigate('home')
        }, {
          label: 'Услуги',
          onClick: () => onNavigate('marketplace')
        }, {
          label: 'Примеры отчётов'
        }]} className="mb-6" data-id="element-21933" />

          <div className="text-center mb-8" data-id="element-21934">
            <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-3" data-id="element-21935">
              Примеры астрологических отчётов
            </h1>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-21936">
              Посмотрите, как выглядят Beta и Premium отчёты для разных услуг
              платформы
            </p>
          </div>

          {/* Service Selector */}
          <div className="flex flex-wrap justify-center gap-2 mb-8" data-id="element-21937">
            {SERVICE_OPTIONS.map(option => <button key={option.value} onClick={() => setSelectedService(option.value)} className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${selectedService === option.value ? 'bg-indigo-600 text-white shadow-md' : 'bg-white text-slate-600 border border-slate-200 hover:border-indigo-200 hover:text-indigo-600'}`} data-id="element-21938">
                {option.icon}
                {option.label}
              </button>)}
          </div>

          {/* Report Type Tabs */}
          <div className="flex justify-center" data-id="element-21939">
            <div className="inline-flex bg-slate-100 rounded-xl p-1" data-id="element-21940">
              {reportTabs.map(tab => <button key={tab.id} onClick={() => setSelectedReportType(tab.id as ReportType)} className={`flex items-center gap-2 px-6 py-3 rounded-lg text-sm font-medium transition-all ${selectedReportType === tab.id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-21941">
                  {tab.icon}
                  {tab.label}
                </button>)}
            </div>
          </div>
        </div>
      </div>

      {/* Report Preview */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-21942">
        <motion.div key={`${selectedService}-${selectedReportType}`} initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.4
      }} data-id="element-21943">
          {selectedReportType === 'beta' ? <BetaReportPreview serviceType={selectedService} onNavigate={onNavigate} data-id="element-21944" /> : <PremiumReportPreview serviceType={selectedService} data-id="element-21945" />}
        </motion.div>

        {/* CTA Section */}
        <div className="mt-12 text-center print:hidden" data-id="element-21946">
          <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-8 max-w-2xl mx-auto" data-id="element-21947">
            <h3 className="text-xl font-bold text-slate-900 mb-3" data-id="element-21948">
              Хотите персональный разбор от профессионала?
            </h3>
            <p className="text-slate-600 mb-6" data-id="element-21949">
              Закажите индивидуальную консультацию с опытным астрологом для
              глубокого анализа вашей карты
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-21950">
              <Button variant="outline" onClick={() => onNavigate('astrologers')} data-id="element-21951">
                <UsersIcon className="w-4 h-4 mr-2" data-id="element-21952" />
                Каталог астрологов
              </Button>
              <Button variant="primary" onClick={() => onNavigate('service-consultation')} data-id="element-21953">
                <SparklesIcon className="w-4 h-4 mr-2" data-id="element-21954" />
                Заказать консультацию
                <ArrowRightIcon className="w-4 h-4 ml-2" data-id="element-21955" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>;
}